import React from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  Alert
} from '@mui/material';
import { CheckCircle, Email, Phone, Schedule } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useTenant } from '../contexts/TenantContext';


const OnboardingSuccess = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();

  return (
    <PageLayout>
      <Container maxWidth="md">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            {/* Success Icon */}
            <CheckCircle 
              sx={{ 
                fontSize: 80, 
                color: 'success.main', 
                mb: 3 
              }} 
            />

            {/* Main Success Message */}
            <Typography variant="h3" gutterBottom color="success.main">
              Application Submitted Successfully!
            </Typography>
            
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
              Thank you for your interest in joining the 5 Fairies Cleaning team!
            </Typography>

            <Divider sx={{ my: 4 }} />

            {/* What's Next Section */}
            <Box sx={{ textAlign: 'left', mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                What happens next?
              </Typography>
              
              <Stack spacing={3} sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Schedule color="primary" />
                  <Box>
                    <Typography variant="h6">Review Process (1-3 business days)</Typography>
                    <Typography color="textSecondary">
                      Our team will review your application, verify your documents, and check your references.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Phone color="primary" />
                  <Box>
                    <Typography variant="h6">Phone Interview</Typography>
                    <Typography color="textSecondary">
                      If you have not been already in contact with us we'll schedule a brief phone interview 
                      to discuss your experience and answer any questions.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Email color="primary" />
                  <Box>
                    <Typography variant="h6">Welcome & Training</Typography>
                    <Typography color="textSecondary">
                      Once approved, you'll receive welcome materials, training resources, 
                      and access to our scheduling platform.
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Important Information */}
            <Alert severity="info" sx={{ mb: 4, textAlign: 'left' }}>
              <Typography variant="h6" gutterBottom>
                Important Information
              </Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>We'll contact you within 1-3 business days via email or phone</li>
                <li>Please ensure your insurance is active if you indicated you have coverage</li>
                <li>Background check processing may take 3-7 business days</li>
                <li>Questions? Email us at <strong>{tenant?.contactEmail}</strong></li>
              </ul>
            </Alert>

            {/* Action Buttons */}
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              justifyContent="center"
              sx={{ mt: 4 }}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate('/')}
              >
                Return to Home
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                size="large"
                href={`mailto:${tenant?.contactEmail}`}
              >
                Contact Us
              </Button>
            </Stack>

            {/* Additional Resources */}
            <Box sx={{ mt: 4, p: 3, backgroundColor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                While You Wait...
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Follow us on social media to stay updated with company news and cleaning tips! 
                You can also explore our service areas and learn more about our commitment to excellence.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>
    </PageLayout>
  );
};

export default OnboardingSuccess;